import React, { useState, useEffect } from 'react';
import { Clock, FileText, AlertCircle, DollarSign, Loader2 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { solicitudesService, cotizacionesService } from '../../services/api';

const InicioView = () => {
  const { user } = useAuth();
  const [estadisticas, setEstadisticas] = useState(null);
  const [solicitudes, setSolicitudes] = useState([]);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        const [solicitudesRes, cotizacionesRes, cotizacionesStatsRes] = await Promise.allSettled([
          solicitudesService.getAll({ limit: 5 }),
          cotizacionesService.getAll({ limit: 5 }),
          cotizacionesService.getStats()
        ]);

        const solicitudesData = solicitudesRes.status === 'fulfilled' ? (solicitudesRes.value?.data || []) : [];
        const cotizacionesData = cotizacionesRes.status === 'fulfilled' ? (cotizacionesRes.value?.data || []) : [];
        const statsData = cotizacionesStatsRes.status === 'fulfilled' ? (cotizacionesStatsRes.value?.data || {}) : {};

        const totalCotizaciones = statsData.total || 0;
        const totalConfirmadas = statsData.confirmadas || 0;

        const pendientesCount = solicitudesData.filter(s =>
          ['por_aprobar_cliente', 'solicitud_nueva', 'cotizando', 'por_comprar_internacional', 'por_comprar_nacional'].includes(s.estado)
        ).length || solicitudesData.length;

        setEstadisticas({
          solicitudesPendientes: pendientesCount,
          cotizacionesPendientes: totalCotizaciones - totalConfirmadas,
          facturasPorVerificar: statsData.en_espera_pago || 0,
          ventasMes: statsData.total_monto || 0
        });

        setSolicitudes(solicitudesData.slice(0, 5));
        setCotizaciones(cotizacionesData.slice(0, 5));
      } catch (error) {
        console.error('Error cargando dashboard:', error);
        setEstadisticas({
          solicitudesPendientes: 0,
          cotizacionesPendientes: 0,
          facturasPorVerificar: 0,
          ventasMes: 0
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 size={32} className="animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl lg:text-2xl font-bold text-slate-900">Dashboard Principal</h2>
        <p className="text-sm lg:text-base text-slate-500">
          Bienvenido, <span className="font-semibold text-slate-700">{user?.nombre || user?.email}</span>
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6 lg:mb-8">
        <div className="bg-white border border-slate-200 rounded-lg p-4 lg:p-6 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
              <Clock size={18} className="text-[#33475b]" />
            </div>
            <span className="text-2xl lg:text-3xl font-bold text-slate-900">{estadisticas?.solicitudesPendientes ?? 0}</span>
          </div>
          <h3 className="text-sm font-medium text-slate-500">Solicitudes Pendientes</h3>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4 lg:p-6 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
              <FileText size={18} className="text-[#33475b]" />
            </div>
            <span className="text-2xl lg:text-3xl font-bold text-slate-900">{estadisticas?.cotizacionesPendientes ?? 0}</span>
          </div>
          <h3 className="text-sm font-medium text-slate-500">Cotizaciones Pendientes</h3>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4 lg:p-6 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center">
              <AlertCircle size={18} className="text-amber-600" />
            </div>
            <span className="text-2xl lg:text-3xl font-bold text-slate-900">{estadisticas?.facturasPorVerificar ?? 0}</span>
          </div>
          <h3 className="text-sm font-medium text-slate-500">Facturas por Verificar</h3>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4 lg:p-6 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
              <DollarSign size={18} className="text-emerald-600" />
            </div>
            <span className="text-lg lg:text-2xl font-bold text-slate-900">${(estadisticas?.ventasMes ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <h3 className="text-sm font-medium text-slate-500">Ventas del Mes</h3>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <div className="rounded-lg border border-slate-200 p-4 lg:p-6 bg-white">
          <h3 className="text-lg lg:text-xl font-bold mb-4 text-slate-900">Solicitudes Recientes</h3>
          <div className="space-y-3">
            {solicitudes.length === 0 ? (
              <p className="text-slate-400 text-center py-4">No hay solicitudes recientes</p>
            ) : solicitudes.slice(0, 5).map(sol => (
              <div key={sol.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 rounded-lg gap-2 bg-slate-50">
                <div className="flex-1">
                  <p className="font-semibold text-sm lg:text-base text-slate-900">{sol.folio || `SOL-${sol.id}`}</p>
                  <p className="text-xs lg:text-sm text-slate-500">{sol.cliente_nombre || sol.empresa_nombre || `Solicitud #${sol.id}`}</p>
                </div>
                <span className={`self-start sm:self-center px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                  sol.estado === 'entregado' || sol.estado === 'listo_para_facturar' ? 'bg-emerald-50 text-emerald-700' :
                  sol.estado === 'rechazado_cliente' ? 'bg-red-50 text-red-700' :
                  'bg-amber-50 text-amber-700'
                }`}>
                  {sol.estado_label || sol.estado || 'Pendiente'}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 p-4 lg:p-6 bg-white">
          <h3 className="text-lg lg:text-xl font-bold mb-4 text-slate-900">Cotizaciones Activas</h3>
          <div className="space-y-3">
            {cotizaciones.length === 0 ? (
              <p className="text-slate-400 text-center py-4">No hay cotizaciones activas</p>
            ) : cotizaciones.slice(0, 5).map(cot => (
              <div key={cot.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 rounded-lg gap-2 bg-slate-50">
                <div className="flex-1">
                  <p className="font-semibold text-sm lg:text-base text-slate-900">{cot.numero || `COT-${cot.id}`}</p>
                  <p className="text-xs lg:text-sm text-slate-500">{cot.cliente_nombre || cot.empresa_nombre || `Cotización #${cot.id}`}</p>
                </div>
                <div className="text-left sm:text-right">
                  <p className="font-bold text-sm lg:text-base text-slate-900">${(cot.total || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  <span className={`text-xs font-semibold ${
                    cot.estado === 'confirmada' || cot.estado === 'pagada' ? 'text-emerald-600' :
                    cot.estado === 'rechazada' ? 'text-red-600' :
                    'text-amber-600'
                  }`}>
                    {cot.estado || 'borrador'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InicioView;
